import { Grid } from "@mui/material";
import "react-dropzone-uploader/dist/styles.css";
import {
  allowedOperations,
  getParams,
  navigate,
  sendGetRequest,
  sendPostRequest,
} from "../utils/utils";
import EditPage from "./component/edit-page";
import { useEffect, useState } from "react";
import AppRoutes from "../routing/routes";
import EasyTextEdit from "./component/text-edit";
import { FormControl, MenuItem, Select, InputLabel } from "@mui/material";

const _ref = {
  uuid: "",
  day: { value: "Monday", touched: true, error: false },
  title: { value: "", touched: false, error: false },
  videoPlayed: { value: "", touched: false, error: false },
  musicListened: { value: "", touched: false, error: false },
  gamesPlayed: { value: "", touched: false, error: false },
  booksRead: { value: "", touched: false, error: false },
  timeSpent: { value: "", touched: false, error: false },
};

const DailyChallenge = () => {
  const { uuid } = getParams();
  var [challenge, setChallenge] = useState(_ref);
  const { canCreate } = allowedOperations();

  const [status, setStatus] = useState({
    error: false,
    loading: false,
    message: null,
  });

  const onValueChange = (value, tag) => {
    let error = false;
    if (tag === "day" && value === "") error = true;
    if (tag === "title" && value === "") error = true;
    if (tag === "videoPlayed" && (value === null || value === "")) error = true;
    if (tag === "musicListened" && (value === null || value === ""))
      error = true;
    if (tag === "gamesPlayed" && (value === null || value === "")) error = true;
    if (tag === "booksRead" && (value === null || value === "")) error = true;
    if (tag === "timeSpent" && (value === null || value === "")) error = true;
    setChallenge({
      ...challenge,
      [tag]: { value: value, touched: true, error: error },
    });
  };

  const checkPropsValidity = () => {
    // Check that title and day are valid (touched and no error)
    const requiredValid = ["title", "day"].every(
      (key) =>
        challenge[key].touched &&
        !challenge[key].error &&
        challenge[key].value !== ""
    );
    // Check that at least one of the other fields is touched and valid
    const otherFields = [
      "videoPlayed",
      "musicListened",
      "gamesPlayed",
      "booksRead",
      "timeSpent",
    ];
    const atLeastOneOtherValid = otherFields.some(
      (key) =>
        challenge[key].touched &&
        !challenge[key].error &&
        challenge[key].value !== null &&
        challenge[key].value !== ""
    );
    return requiredValid && atLeastOneOtherValid;
  };

  useEffect(() => {
    sendGetRequest(
      `challenges?uuid=${uuid}`,
      (loading) => setStatus({ ...{ loading: loading } }),
      (data) => {
        if (data) {
          data = Object.keys(data).reduce((acc, key) => {
            acc[key] = { value: `${data[key]}`, touched: true, error: false };
            return acc;
          }, {});
          setChallenge(data);
        }
      }
    );
    return () => setChallenge(_ref);
    // eslint-disable-next-line
  }, [uuid]);

  const createUpdate = () => {
    const data = Object.keys(challenge).reduce((acc, key) => {
      if (key !== "uuid") acc[key] = challenge[key].value || 0;
      return acc;
    }, {});
    if (uuid) {
      sendPostRequest(
        "challenges",
        { uuid, ...data },
        (loading) => setStatus({ ...{ loading: loading } }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        }
      );
    } else {
      sendPostRequest(
        `challenges?uuid=${uuid}`,
        data,
        (loading) => setStatus({ ...{ loading: loading } }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        }
      );
    }
  };

  return (
    <EditPage
      title={"Daily Challenge"}
      status={status}
      btnLabel={canCreate ? (uuid ? "Update Now" : "Add Now") : null}
      onSave={() => createUpdate()}
      spacing={5}
      onBack={() =>
        navigate(`${AppRoutes.appSettings}/${AppRoutes.challenges}`, true)
      }
      validated={checkPropsValidity()}
      onFinish={() => {
        setStatus({ ...{ loading: false, message: null, error: false } });
        if (!status.error) {
          setChallenge({ ..._ref });
          navigate(`${AppRoutes.appSettings}/${AppRoutes.challenges}`, true);
        }
      }}
    >
      <Grid item md={3} xs={12} alignItems="right" alignContent={"right"}>
        <FormControl sx={{ minWidth: 250 }} fullWidth>
          <InputLabel id="day-label">Challenge Day</InputLabel>
          <Select
            value={challenge.day.value}
            label="Challenge Day"
            labelId="day-label"
            id="day"
            disabled={status.loading}
            onChange={(e) => onValueChange(e.target.value, "day")}
          >
            {[
              { name: "Monday", value: "Monday" },
              { name: "Tuesday", value: "Tuesday" },
              { name: "Wednesday", value: "Wednesday" },
              { name: "Thursday", value: "Thursday" },
              { name: "Friday", value: "Friday" },
              { name: "Saturday", value: "Saturday" },
              { name: "Sunday", value: "Sunday" },
            ].map((souces) => {
              return (
                <MenuItem value={souces.value} id={souces.value}>
                  &nbsp;&nbsp;&nbsp;{souces.name}
                  <br />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid item md={6} xs={12}>
        <EasyTextEdit
          placeholder="Challenge Title"
          label="Challenge Title"
          type="text"
          textField={true}
          error={challenge.title !== "" && challenge.title === null}
          id="title"
          errorText={
            challenge.title.error
              ? "Make sure you fill in the challenge title"
              : ""
          }
          disabled={status.loading}
          value={challenge.title.value}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={3} xs={12}>
        <EasyTextEdit
          placeholder="Number of videos to play"
          label="Number of Videos to play"
          type="number"
          textField={true}
          error={challenge.videoPlayed.error}
          id="videoPlayed"
          errorText={
            challenge.videoPlayed.error
              ? "Make sure you fill in number of videos to play"
              : ""
          }
          disabled={status.loading}
          value={challenge.videoPlayed.value}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={3} xs={12}>
        <EasyTextEdit
          placeholder="Number of books to read"
          label="Number of Books to read"
          type="number"
          textField={true}
          error={challenge.booksRead.error}
          id="booksRead"
          errorText={
            challenge.booksRead.error
              ? "Make sure you fill in number of books to read"
              : ""
          }
          disabled={status.loading}
          value={challenge.booksRead.value}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={3} xs={12}>
        <EasyTextEdit
          placeholder="Number of Songs to listen"
          label="Number of songs to listen"
          type="number"
          textField={true}
          error={challenge.musicListened.error}
          id="musicListened"
          errorText={
            challenge.musicListened.error
              ? "Make sure you fill in number of music to listen"
              : ""
          }
          disabled={status.loading}
          value={challenge.musicListened.value}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={3} xs={12}>
        <EasyTextEdit
          placeholder="Number of Games to play"
          label="Number of games to play"
          type="number"
          textField={true}
          error={challenge.gamesPlayed.error}
          id="gamesPlayed"
          errorText={
            challenge.gamesPlayed.error
              ? "Make sure you fill in number of games to play"
              : ""
          }
          disabled={status.loading}
          value={challenge.gamesPlayed.value}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={3} xs={12}>
        <EasyTextEdit
          placeholder="Minutes to Spend"
          label="Time Spent"
          type="number"
          textField={true}
          error={challenge.timeSpent.error}
          id="timeSpent"
          errorText={
            challenge.timeSpent.error
              ? "Make sure you fill in required time to spend"
              : ""
          }
          disabled={status.loading}
          value={challenge.timeSpent.value}
          onChange={onValueChange}
        />
      </Grid>
    </EditPage>
  );
};

export default DailyChallenge;
